/**
 * <free-shipping-note>
 *
 * Shows "inkl. gratis Versand" only when the free-shipping rules (published by the Versandregeln app
 * from the real Shopify shipping profiles) say shipping is free for the selected country and for the
 * cart subtotal including the product being added. Everything else (no rules, unknown country,
 * different currency, below the threshold) keeps the legally required "zzgl. Versandkosten" text
 * that is rendered by default.
 *
 * Rules shape (Versand-Regeln app, from the automatic Free-shipping discounts):
 *   { shopCurrency: 'EUR', countries: { AT: { currency: 'EUR', freeFrom: 69 }, CH: {...}, ROW: {...} } }
 *   freeFrom: 0 = always free. A country without an entry never gets free shipping.
 *
 * Shipping classes that are free on their own (products in data-free-class-products, for the visitor's country):
 * "inkl. gratis Versand" while the cart contains nothing but such products (plus digital items); any other
 * product in the cart brings the threshold rule above back.
 *
 * A selected variant that does not require shipping (data-requires-shipping="false" — a digital gift
 * card, for example) always shows "Versand per E-Mail" instead, independent of the threshold, the
 * country, or whether any rule exists at all: it is never physically shipped, so "gratis Versand" would
 * be misleading.
 */
if (!customElements.get('free-shipping-note')) {
  class FreeShippingNote extends HTMLElement {
    connectedCallback() {
      this.country = this.dataset.country;
      this.currency = this.dataset.currency;
      this.variantPriceCents = parseInt(this.dataset.variantPrice, 10) || 0;
      this.requiresShipping = this.dataset.requiresShipping === 'true';
      this.digitalEl = this.querySelector('.pdp-shipping__digital');
      this.freeEl = this.querySelector('.pdp-shipping__free');
      this.paidEl = this.querySelector('.pdp-shipping__paid');
      this.requestId = 0;
      this.productId = this.dataset.productId;
      try {
        this.freeClassProducts = JSON.parse(this.querySelector('[data-free-class-products]')?.textContent || 'null') || {};
      } catch (error) {
        this.freeClassProducts = {};
      }

      try {
        this.rule = this.findRule(JSON.parse(this.querySelector('[data-free-shipping-rules]')?.textContent || 'null'));
      } catch (error) {
        this.rule = null;
      }
      if (this.rule) this.ensureThresholdLabel();

      this.productInfo = this.closest('product-info');
      this.scheduleCheck = FoxTheme.utils.debounce(() => this.check(), 150);

      this.onVariantChanged = (event) => {
        const variant = event.detail?.variant;
        if (Number.isFinite(variant?.price)) this.variantPriceCents = variant.price;
        if (typeof variant?.requires_shipping === 'boolean') this.requiresShipping = variant.requires_shipping;
        this.scheduleCheck();
      };
      document.addEventListener('variant:changed', this.onVariantChanged);

      this.onQuantityEvent = (event) => {
        if (event.target.closest?.('quantity-input')) this.scheduleCheck();
      };
      this.productInfo?.addEventListener('change', this.onQuantityEvent);
      this.productInfo?.addEventListener('click', this.onQuantityEvent);

      this.cartUpdateUnsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.scheduleCheck
      );

      this.check();
    }

    disconnectedCallback() {
      document.removeEventListener('variant:changed', this.onVariantChanged);
      this.productInfo?.removeEventListener('change', this.onQuantityEvent);
      this.productInfo?.removeEventListener('click', this.onQuantityEvent);
      this.cartUpdateUnsubscriber?.();
    }

    // Threshold in the cart's currency, or null when free shipping does not apply / cannot be judged.
    // Discount minimums are set in the shop currency; Shopify converts them for other markets, so we
    // convert with the same live rate (Shopify.currency.rate) instead of guessing.
    findRule(rules) {
      const countries = rules?.countries;
      if (!countries) return null;
      const rule = countries[this.country] || countries.ROW;
      if (!rule || rule.freeFrom === null || rule.freeFrom === undefined) return null;

      if (rule.currency === this.currency) return { freeFrom: rule.freeFrom, converted: false };
      if (rule.currency === this.dataset.shopCurrency) {
        const rate = parseFloat(window.Shopify?.currency?.rate);
        if (Number.isFinite(rate) && rate > 0) return { freeFrom: rule.freeFrom * rate, converted: true };
      }
      return null;
    }

    // "Gratis ab CHF 175" for converted thresholds (the server only renders the same-currency case).
    ensureThresholdLabel() {
      if (!this.rule.converted || this.querySelector('.pdp-shipping__threshold')) return;
      const template = this.dataset.freeFromTemplate;
      if (!template || !(this.rule.freeFrom > 0)) return;

      let symbol = this.currency;
      try {
        symbol =
          new Intl.NumberFormat(document.documentElement.lang || undefined, {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol'
          })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value || this.currency;
      } catch (error) {}

      const label = document.createElement('span');
      label.className = 'pdp-shipping__threshold';
      label.textContent = ` – ${template.replace('__AMOUNT__', `${symbol} ${Math.round(this.rule.freeFrom)}`)}`;
      this.paidEl.append(label);
    }

    get quantity() {
      const value = parseInt(this.productInfo?.querySelector('quantity-input input')?.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 1;
    }

    async getCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        return await response.json();
      } catch (error) {
        return null;
      }
    }

    // A product ships free on its own in this country (its shipping class has a 0 rate here).
    isFreeClass(productId) {
      const countries = this.freeClassProducts[String(productId)];
      return Array.isArray(countries) && (countries.includes(this.country) || countries.includes('ROW'));
    }

    async check() {
      const requestId = ++this.requestId;

      // A variant that does not require shipping is never physically shipped at all — independent of the
      // threshold, the selected country, or whether any free-shipping rule exists.
      if (!this.requiresShipping) {
        this.render('digital');
        return;
      }
      const thisIsFreeClass = this.isFreeClass(this.productId);
      if (!this.rule && !thisIsFreeClass) {
        this.render('paid');
        return;
      }

      const cart = await this.getCart();
      // A newer check started while the cart request was running
      if (requestId !== this.requestId) return;

      // Only products that ship free on their own (Farbmuster, …) in the cart: free whatever the value.
      // Any other product brings the normal rules back.
      if (thisIsFreeClass && (cart?.items || []).every((item) => !item.requires_shipping || this.isFreeClass(item.product_id))) {
        this.render('free');
        return;
      }
      if (!this.rule) {
        this.render('paid');
        return;
      }

      const subtotal = ((cart?.total_price || 0) + this.variantPriceCents * this.quantity) / 100;
      this.render(subtotal >= this.rule.freeFrom ? 'free' : 'paid');
    }

    // state: 'digital' | 'free' | 'paid'
    render(state) {
      this.digitalEl.hidden = state !== 'digital';
      this.freeEl.hidden = state !== 'free';
      this.paidEl.hidden = state !== 'paid';
    }
  }

  customElements.define('free-shipping-note', FreeShippingNote);
}
